import { Brain, FileText, MessageSquare, BarChart, Paperclip, Mic, Send } from 'lucide-react'

interface HomeViewProps {
  onStartAction: (action: string) => void
  onUploadClick?: () => void
  onVoiceClick?: () => void
}

const actionCards = [
  {
    id: 'upload',
    icon: FileText,
    title: 'Upload documents',
    desc: 'Add pitch decks, one-pagers, etc.',
  },
  {
    id: 'interview',
    icon: MessageSquare,
    title: 'Tell me about startup',
    desc: "I'll ask questions to understand",
  },
  {
    id: 'generate',
    icon: BarChart,
    title: 'Generate artifacts',
    desc: 'Create pitch decks, canvases, etc.',
  },
]

export function HomeView({ onStartAction, onUploadClick, onVoiceClick }: HomeViewProps) {
  return (
    <div className="flex-1 overflow-y-auto w-full p-4 md:p-6">
      <div className="max-w-4xl mx-auto min-h-full flex flex-col gap-4 md:gap-8">
        {/* Hero */}
        <div className="text-center space-y-2 md:space-y-4 pt-1 md:pt-8">
          <div className="flex h-14 w-14 md:h-20 md:w-20 bg-slate-100 dark:bg-secondary/50 rounded-2xl md:rounded-3xl mx-auto items-center justify-center text-primary border border-slate-200 dark:border-white/5 shadow-2xl shadow-primary/10">
            <Brain className="h-7 w-7 md:h-10 md:w-10" />
          </div>
          <h2 className="text-xl md:text-4xl font-bold tracking-tight px-2">
            Ready to build your context.
          </h2>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-6 w-full">
          {actionCards.map((card, idx) => (
            <button
              key={card.id}
              className={`text-left rounded-2xl md:rounded-3xl bg-[#f3f5f8] dark:bg-secondary hover:bg-[#eef1f8] dark:hover:bg-tertiary/70 border border-[#f3f5f8] dark:border-white/15 hover:border-[#c7d4f8] dark:hover:border-primary/35 transition-all group ${
                idx === 2
                  ? 'col-span-2 md:col-span-1'
                  : ''
              } flex flex-row md:flex-col items-center md:items-start gap-3 md:gap-0 p-3.5 md:p-6 min-h-[88px] md:min-h-[192px] justify-start md:justify-between`}
              onClick={() => onStartAction(card.id)}
            >
              <div className="h-9 w-9 md:h-12 md:w-12 rounded-full bg-white dark:bg-background border border-[#dde3f2] dark:border-transparent flex items-center justify-center text-primary group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-colors duration-300 shrink-0">
                <card.icon className="h-5 w-5 md:h-6 md:w-6" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-sm md:text-lg mb-0.5 md:mb-1 leading-tight">{card.title}</h3>
                <p className="text-[11px] md:text-sm text-muted-foreground leading-snug line-clamp-2 md:line-clamp-none">
                  {card.desc}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Bottom Input Bar */}
        <div className="w-full max-w-2xl mt-auto pt-2 md:pt-6 relative group mx-auto">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-accent-amber/20 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-500" />
          <div className="relative flex items-center bg-white dark:bg-secondary rounded-full border border-slate-300 dark:border-white/20 px-4 md:px-6 py-3 md:py-4 shadow-lg backdrop-blur-sm">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors mr-3 md:mr-4"
              onClick={onUploadClick}
            >
              <Paperclip className="h-5 w-5" />
            </button>
            <input
              className="flex-1 bg-transparent border-none outline-none text-sm md:text-base placeholder:text-muted-foreground/70"
              placeholder="I want to generate artifacts..."
            />
            <button
              type="button"
              className="p-2 mr-1 text-muted-foreground hover:text-primary transition-colors"
              onClick={onVoiceClick}
            >
              <Mic className="h-5 w-5" />
            </button>
            <button className="h-8 w-8 md:h-10 md:w-10 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center text-white transition-colors ml-2 shadow-lg shadow-primary/20">
              <Send className="h-4 w-4 md:h-5 md:w-5 ml-0.5" />
            </button>
          </div>
          <p className="text-center text-[10px] text-muted-foreground mt-3">
            ContextMD can make mistakes. Please verify important information.
          </p>
        </div>
      </div>
    </div>
  )
}
